import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetClose
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SERVICES, resolveLocale } from "@/data/serviceDetails";
import { ChevronDown } from "lucide-react";

export const MobileNav = () => {
  const { t, i18n } = useTranslation();
  const [isCorporateOpen, setIsCorporateOpen] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const locale = resolveLocale(i18n.language || "en");
  const projectMenuItems = useMemo(() =>
    SERVICES.map((service) => ({
      id: service.id,
      href: `/projeler/${service.slug}`,
      label: service.translations[locale]?.name ?? service.translations.en.name,
    })),
  [locale]);
  const navLinks = [
    { href: "/#services", label: t("nav.services") },
    { href: "/#partners", label: t("nav.partners") },
    { href: "/#process", label: t("nav.process") },
    { href: "/#regions", label: t("nav.regions") },
    { href: "/blog", label: t("nav.blog", "Blog") },
    { href: "/#contact", label: t("nav.contact") },
  ];
  const aboutMenuItems = [
    { key: "who_we_are", label: t("nav.about_menu.who_we_are"), href: "/kurumsal#who-we-are" },
    { key: "vision", label: t("nav.about_menu.vision"), href: "/kurumsal#vision" },
    { key: "mission", label: t("nav.about_menu.mission"), href: "/kurumsal#mission" },
    { key: "values", label: t("nav.about_menu.values"), href: "/kurumsal#values" },
  ];
  const sheetSide = i18n.dir() === 'rtl' ? 'right' : 'left';
  return (
    <div className="md:hidden">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" aria-label={t('nav.toggle')}>
            <Menu className="h-6 w-6" />
            <span className="sr-only">{t('nav.toggle')}</span>
          </Button>
        </SheetTrigger>
        <SheetContent side={sheetSide as any} className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 pt-6">
            <img src="/images/logo/INS_1_192.png" srcSet="/images/logo/INS_1_192.png 192w, /images/logo/INS_1_512.png 512w" sizes="(min-width: 768px) 160px, 160px" alt="INS Organization" className="header-logo" width={192} height={192} decoding="async" loading="eager" />
          </div>
          <nav className="mt-4 flex-1 overflow-y-auto px-6 pb-6">
            <div className="flex flex-col gap-6 text-lg">
              <div className="rounded-xl border border-neutral-200/70 bg-white/80">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-lg font-semibold text-neutral-900"
                  onClick={() => setIsCorporateOpen((prev) => !prev)}
                  aria-expanded={isCorporateOpen}
                >
                  <span>{t("nav.about_menu.label")}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-brand-turquoise transition-transform ${isCorporateOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isCorporateOpen ? (
                  <div className="flex flex-col divide-y divide-neutral-200/70 border-t border-neutral-200/70">
                    {aboutMenuItems.map((item) => (
                      <SheetClose asChild key={item.key}>
                        <Link
                          to={item.href}
                          className="px-4 py-3 text-base text-neutral-600 transition hover:bg-brand-turquoise/10 hover:text-brand-turquoise"
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-neutral-200/70 bg-white/80">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-lg font-semibold text-neutral-900"
                  onClick={() => setIsProjectsOpen((prev) => !prev)}
                  aria-expanded={isProjectsOpen}
                >
                  <span>{t("nav.projects")}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-brand-turquoise transition-transform ${isProjectsOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isProjectsOpen ? (
                  <div className="flex max-h-80 flex-col overflow-y-auto divide-y divide-neutral-200/70 border-t border-neutral-200/70">
                    {projectMenuItems.map((item) => (
                      <SheetClose asChild key={item.id}>
                        <Link
                          to={item.href}
                          className="px-4 py-3 text-base text-neutral-600 transition hover:bg-brand-turquoise/10 hover:text-brand-turquoise"
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                ) : null}
              </div>

              {navLinks.map((link) => (
                <SheetClose asChild key={link.href}>
                  <Link
                    to={link.href}
                    className="rounded-xl border border-neutral-200/70 bg-white/80 px-4 py-3 text-lg font-medium text-neutral-900 transition hover:bg-brand-turquoise/10 hover:text-brand-turquoise"
                  >
                    {link.label}
                  </Link>
                </SheetClose>
              ))}
            </div>
          </nav>
          {/* Language switcher handled in desktop header */}
        </SheetContent>
      </Sheet>
    </div>
  );
};
